import React from 'react';

/**
 * Small shared pieces for the compliance dashboard. Inline styles, matching the
 * existing pages — no CSS framework in this app.
 *
 * Palette values come from the dataviz reference palette (validated for the
 * white app surface: categorical slots 1-2 pass all six checks, worst adjacent
 * CVD dE 24.7).
 */

/**
 * Every colour is a CSS variable with its light value as the fallback.
 *
 * That is what lets one page be dark without restyling the other six. The light
 * values live on :root in App.css and are what every page gets; the R&M
 * dashboard sets `data-rm-theme="dark"` on <body>, which redefines the same
 * variables for everything inside it — including the charts, since `var()`
 * resolves in SVG fills, and the detail drawer, which is position:fixed off the
 * body.
 *
 * Keep the fallbacks in step with App.css: they are what renders if the
 * stylesheet has not loaded, and what server-side rendering sees in tests.
 */
export const COLORS = {
    surface: 'var(--ui-surface, #ffffff)',
    plane: 'var(--ui-plane, #f9f9f7)',
    ink: 'var(--ui-ink, #0b0b0b)',
    ink2: 'var(--ui-ink2, #52514e)',
    muted: 'var(--ui-muted, #898781)',
    grid: 'var(--ui-grid, #e1e0d9)',
    axis: 'var(--ui-axis, #c3c2b7)',
    border: 'var(--ui-border, rgba(11,11,11,0.10))',
    navy: 'var(--ui-navy, #1a1a2e)',
    link: 'var(--ui-link, #2a78d6)',
    series: [
        'var(--ui-series-1, #2a78d6)',
        'var(--ui-series-2, #eb6834)',
        'var(--ui-series-3, #1baf7a)',
        'var(--ui-series-4, #eda100)',
    ],
    // Status palette is reserved — never reused as a series colour.
    good: 'var(--ui-good, #0ca30c)',
    warning: 'var(--ui-warning, #fab219)',
    serious: 'var(--ui-serious, #ec835a)',
    critical: 'var(--ui-critical, #d03b3b)',
};

export const money = v => v == null ? '' :
    '$' + Number(v).toLocaleString('en-AU', { maximumFractionDigits: 0 });

export const date = d => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const dateTime = d => {
    if (!d) return '';
    return new Date(d).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export function Panel({ title, subtitle, right, children, style }) {
    return (
        <section style={{
            background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            borderRadius: 8, marginBottom: 20, ...style,
        }}>
            {(title || right) && (
                <header style={{
                    display: 'flex', alignItems: 'baseline', gap: 12,
                    padding: '12px 16px', borderBottom: `1px solid ${COLORS.grid}`,
                }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: COLORS.ink }}>{title}</h2>
                        {subtitle && <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>{subtitle}</div>}
                    </div>
                    <div style={{ marginLeft: 'auto' }}>{right}</div>
                </header>
            )}
            <div style={{ padding: 16 }}>{children}</div>
        </section>
    );
}

const OUTCOME_STYLE = {
    change: { bg: '#e8f1fc', fg: '#1c5cab' },
    updated: { bg: '#e4f6e4', fg: '#046804' },
    'dry-run': { bg: '#eef0f4', fg: '#52514e' },
    skipped: { bg: '#f2f1ee', fg: '#52514e' },
    'skipped-already-set': { bg: '#f2f1ee', fg: '#52514e' },
    'not-eligible': { bg: '#f2f1ee', fg: '#52514e' },
    blocked: { bg: '#fdefe8', fg: '#9c4620' },
    failed: { bg: '#fbe9e9', fg: '#a12626' },
    unverified: { bg: '#fff4d9', fg: '#8a6100' },
};

/** Outcome/state chip. Always paired with its text label — never colour alone. */
export function Badge({ value, title }) {
    const s = OUTCOME_STYLE[value] || { bg: '#f2f1ee', fg: COLORS.ink2 };
    return (
        <span title={title} style={{
            background: s.bg, color: s.fg, fontSize: 11, fontWeight: 600,
            padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap',
        }}>{value}</span>
    );
}

export function Button({ children, kind = 'default', disabled, ...rest }) {
    const kinds = {
        default: { bg: '#fff', fg: COLORS.ink, border: COLORS.axis },
        primary: { bg: COLORS.link, fg: '#fff', border: COLORS.link },
        danger: { bg: COLORS.critical, fg: '#fff', border: COLORS.critical },
        ghost: { bg: 'transparent', fg: COLORS.ink2, border: 'transparent' },
    };
    const k = kinds[kind] || kinds.default;
    return (
        <button disabled={disabled} {...rest} style={{
            background: disabled ? '#f2f1ee' : k.bg,
            color: disabled ? COLORS.muted : k.fg,
            border: `1px solid ${disabled ? COLORS.grid : k.border}`,
            padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer', ...rest.style,
        }}>{children}</button>
    );
}

export function Field({ label, hint, children, width }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: COLORS.ink2, width }}>
            <span style={{ fontWeight: 600 }}>{label}</span>
            {children}
            {hint && <span style={{ color: COLORS.muted, fontSize: 11 }}>{hint}</span>}
        </label>
    );
}

export const inputStyle = {
    padding: '6px 8px', border: `1px solid ${COLORS.axis}`, borderRadius: 5,
    fontSize: 13, fontFamily: 'inherit', background: '#fff', color: COLORS.ink,
};

export function Table({ columns, rows, empty = 'Nothing to show', rowKey, maxHeight = 520, onRowClick }) {
    if (!rows || rows.length === 0) {
        return <div style={{ color: COLORS.muted, fontSize: 13, padding: '8px 0' }}>{empty}</div>;
    }
    return (
        <div style={{ overflow: 'auto', maxHeight, border: `1px solid ${COLORS.grid}`, borderRadius: 6 }}>
            {/* border-collapse must be `separate` for a sticky <th> to hold —
                with `collapse` the header scrolls away in most browsers. */}
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12.5 }}>
                <thead>
                    <tr>
                        {columns.map(c => (
                            <th key={c.key} style={{
                                position: 'sticky', top: 0, background: COLORS.plane, zIndex: 2,
                                textAlign: c.align || 'left', padding: '8px 10px', fontWeight: 600,
                                color: COLORS.ink2, whiteSpace: 'nowrap',
                                // A sticky cell paints over the rows beneath it, so the
                                // divider has to be a shadow rather than a table border.
                                boxShadow: `inset 0 -1px 0 ${COLORS.axis}`,
                            }}>{c.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={rowKey ? rowKey(r, i) : i}
                            onClick={onRowClick ? () => onRowClick(r) : undefined}
                            style={{
                                borderBottom: `1px solid ${COLORS.grid}`,
                                cursor: onRowClick ? 'pointer' : 'default',
                                background: i % 2 ? COLORS.plane : COLORS.surface,
                            }}>
                            {columns.map(c => (
                                <td key={c.key} style={{
                                    padding: '6px 10px', textAlign: c.align || 'left',
                                    fontVariantNumeric: c.align === 'right' ? 'tabular-nums' : undefined,
                                    whiteSpace: c.wrap ? 'normal' : 'nowrap',
                                    color: COLORS.ink,
                                }}>{c.render ? c.render(r) : r[c.key]}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function StatTile({ label, value, sub, tone }) {
    const toneColor = tone === 'bad' ? 'var(--ui-tone-bad-fg, #d03b3b)'
        : tone === 'warn' ? 'var(--ui-tone-warn-fg, #8a6100)'
        : tone === 'good' ? 'var(--ui-tone-good-fg, #046804)' : COLORS.ink;
    return (
        <div style={{
            border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '12px 14px',
            background: COLORS.surface, minWidth: 130,
        }}>
            <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: toneColor, lineHeight: 1.15, marginTop: 4 }}>{value}</div>
            {sub && <div style={{ fontSize: 11.5, color: COLORS.ink2, marginTop: 2 }}>{sub}</div>}
        </div>
    );
}

export function Banner({ tone = 'info', children }) {
    const tones = {
        info: { bg: 'var(--ui-banner-info-bg, #e8f1fc)', border: 'var(--ui-banner-info-bd, #b7d3f6)', fg: 'var(--ui-banner-info-fg, #1c5cab)' },
        warn: { bg: 'var(--ui-banner-warn-bg, #fff4d9)', border: 'var(--ui-banner-warn-bd, #f2dca2)', fg: 'var(--ui-banner-warn-fg, #8a6100)' },
        danger: { bg: 'var(--ui-banner-danger-bg, #fbe9e9)', border: 'var(--ui-banner-danger-bd, #f0c4c4)', fg: 'var(--ui-banner-danger-fg, #a12626)' },
        good: { bg: 'var(--ui-banner-good-bg, #e4f6e4)', border: 'var(--ui-banner-good-bd, #bfe6bf)', fg: 'var(--ui-banner-good-fg, #046804)' },
    };
    const t = tones[tone] || tones.info;
    return (
        <div style={{
            background: t.bg, border: `1px solid ${t.border}`, color: t.fg,
            padding: '9px 12px', borderRadius: 6, fontSize: 13, marginBottom: 14,
        }}>{children}</div>
    );
}

export function Spinner({ label = 'Loading' }) {
    return <div style={{ color: COLORS.muted, fontSize: 13, padding: '10px 0' }}>{label}…</div>;
}
